import * as React from "react"
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "gatsby-interface"
import { Waypoint } from "react-waypoint"
import { debounce } from "@modules/ui/utils/debounce"
import { branchList as text } from "@modules/locales/default.js"

export type BranchesComboboxProps = {
  branches: string[]
  onSearchCriteriaChange: (searchCriteria: string) => void
  selectedBranch?: string
  inputId: string
  "aria-labelledby": string
  "aria-label": string
  "aria-describedby"?: string
  onBranchChange: (branch: string) => void
  onLoadMore: () => void
}

export function BranchesCombobox({
  branches,
  onSearchCriteriaChange,
  selectedBranch = "",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  inputId,
  onBranchChange,
  onLoadMore,
}: BranchesComboboxProps) {
  // Controls search input value
  const [searchNeedle, setSearchNeedle] = React.useState<string>(selectedBranch)
  const popoverRef = React.useRef<HTMLDivElement>(null)

  const handleBranchChange = (nextBranch: string) => {
    setSearchNeedle(nextBranch)
    onBranchChange(nextBranch)
  }

  const updateSearchCriteria = (searchText: string) => {
    return onSearchCriteriaChange(searchText)
  }

  const debouncedUpdateSearchCriteria = debounce(updateSearchCriteria, 400)

  return (
    <Combobox onSelect={handleBranchChange}>
      <ComboboxInput
        id={inputId}
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const searchText = e.target.value
          // Update controlled input's value
          setSearchNeedle(searchText)
          // And debounce search request
          return debouncedUpdateSearchCriteria(searchText)
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          /**
           * The user can type anything in the input field and proceed to another field
           * In such cases we want to restore input value to the currently selected branch
           */
          if (!popoverRef.current) {
            return
          }

          // We have to check if the newly focused element is not contained in the combobox popover
          if (
            searchNeedle !== selectedBranch &&
            !popoverRef.current.contains(e.relatedTarget as Node)
          ) {
            // Restore currently selected branch if user typed anything else and left the input
            setSearchNeedle(selectedBranch)
            onSearchCriteriaChange(selectedBranch)
          }
        }}
        data-cy="branch-list-search"
        placeholder={text.labels.searchPlaceholder}
        selectedOptionLabel={selectedBranch}
        value={searchNeedle}
        showToggleButton
        toggleButtonAriaLabel={text.actions.showOptions}
        autoComplete="off"
      />
      <ComboboxPopover ref={popoverRef} portal>
        {branches.length > 0 ? (
          <ComboboxList
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            data-cy="branch-list"
          >
            {branches.map(branch => {
              return (
                <ComboboxOption
                  key={branch}
                  value={branch}
                  selected={selectedBranch === branch}
                  highlightMatches={true}
                  data-cy={`branch-list-item-` + branch}
                />
              )
            })}
          </ComboboxList>
        ) : (
          <p css={theme => ({ margin: `${theme.space[3]} ${theme.space[5]}` })}>
            {text.messages.zeroResults}
          </p>
        )}
        <Waypoint onEnter={onLoadMore} />
      </ComboboxPopover>
    </Combobox>
  )
}
